import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { sdsColors, sdsRadius, sdsBtnBase } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import heroImg from '../../assets/images/PoweredByDQ/WorkforceHero.png';
import HomeHeroDqLogo from '../../assets/images/PoweredByDQ/HomeHeroDqLogo.png';
import step4Img from '../../assets/images/PoweredByDQ/SchoolCosReadinessStep2.jpg';
import demoVideo from '../../assets/videos/PoweredByDQ/WorkforceFrameworkCreatorDemo.mp4';
import workforceExecutiveDashImg from '../../assets/images/PoweredByDQ/WorkforceExecutiveDash.png';

/*
 * Workforce AI Readiness — rebuilt to match the design, styling and content
 * of the mockup HTML ("Powered by DQ - Workforce AI Readiness.html") and the
 * approved visual system.
 */

const MOBILE = '@media (max-width: 820px)';
const NARROW = '@media (max-width: 1180px)';

const useStyles = makeStyles(() => ({
    accent: { color: sdsColors.accent },
    gutter: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: '0 56px',
        [NARROW]: { padding: '0 32px' },
        [MOBILE]: { padding: '0 20px' },
    },

    // ── SECTION 0: HERO ────────────────────────────────────────────────────────
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
        overflow: 'hidden',
    },
    heroContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '28px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    heroTextCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        color: '#1B2430',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
    },
    heroBtn: {
        justifySelf: 'start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
        },
    },
    heroArtCol: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        [MOBILE]: {
            maxWidth: '100%',
        },
    },
    heroImg: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    // ── COMMON SECTION UTILS ──────────────────────────────────────────────────
    sectionEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    sectionTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },

    // ── SECTION 1: CONFIGURATIONS ─────────────────────────────────────────────
    configSection: {
        background: '#FFFFFF',
        padding: 'clamp(36px, 4.5vw, 56px) 0',
    },
    configGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 40,
    },
    configCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 'clamp(24px, 2.8vw, 36px)',
        borderRadius: 24,
    },
    configTag: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    configCardTitle: {
        fontSize: 'clamp(22px, 2.2vw, 28px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    configCardIntro: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    configFigure: {
        margin: 0,
        padding: '18px 18px 16px',
        borderRadius: 16,
        background: '#FFFFFF',
        border: '1px solid #E6E9EE',
        display: 'grid',
        gap: 14,
    },
    configStagesRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: 0,
        alignItems: 'start',
    },
    stageItem: {
        position: 'relative',
        display: 'grid',
        justifyItems: 'center',
        gap: 8,
        minWidth: 0,
    },
    stageLine: {
        position: 'absolute',
        top: 21,
        left: '50%',
        width: '100%',
        height: 2,
    },
    stageCircle: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        fontSize: 13,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        zIndex: 1,
    },
    stageName: {
        fontSize: 12,
        lineHeight: 1.25,
        fontWeight: 700,
        textAlign: 'center',
    },
    stageAddedBadge: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#ED6628',
        whiteSpace: 'nowrap',
    },
    configFigCaption: {
        fontSize: 12.5,
        lineHeight: 1.45,
        color: '#5B6470',
        paddingTop: 12,
        borderTop: '1px solid #EEF1F4',
    },
    configBestFor: {
        display: 'grid',
        gap: 10,
        marginTop: 'auto',
        paddingTop: 16,
        borderTop: '1px solid #E6E9EE',
        fontSize: 14,
        lineHeight: 1.55,
        color: '#5B6470',
    },
    configCtaBtn: {
        justifySelf: 'start',
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        textDecoration: 'none',
        fontSize: 14.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: '1px solid transparent',
        transition: 'all 200ms ease',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },

    // ── SECTION 2: COMPARISON & ADD-ONS ───────────────────────────────────────
    compareSection: {
        background: '#F4F6F9',
        padding: 'clamp(36px, 4.5vw, 56px) 0',
    },
    tableWrapper: {
        overflowX: 'auto',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        background: '#FFFFFF',
    },
    tableInner: {
        minWidth: 560,
    },
    tableHeaderRow: {
        display: 'grid',
        gridTemplateColumns: '1.8fr 1fr 1fr',
        gap: 16,
        padding: '16px 24px',
        background: '#F8F9FB',
        borderBottom: '1px solid #E6E9EE',
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#6B7280',
    },
    tableDataRow: {
        display: 'grid',
        gridTemplateColumns: '1.8fr 1fr 1fr',
        gap: 16,
        padding: '15px 24px',
        borderBottom: '1px solid #EEF1F4',
        fontSize: 14.5,
        alignItems: 'center',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    compareNoteBox: {
        margin: '20px 0 0',
        padding: '18px 22px',
        borderRadius: 16,
        background: '#F4F6F9',
        border: '1px solid #E6E9EE',
        fontSize: 14.5,
        lineHeight: 1.55,
        color: '#5B6470',
    },
    addonsCard: {
        marginTop: 'clamp(32px, 4vw, 48px)',
        background: '#FFFFFF',
        padding: 'clamp(24px, 3vw, 40px)',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
    },
    addonsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: '12px 32px',
    },
    addonItem: {
        margin: 0,
        padding: '12px 0',
        borderTop: '1px solid #EEF1F4',
        fontSize: 14.5,
        lineHeight: 1.55,
        color: '#5B6470',
    },

    // ── SECTION 3: STEPPER ────────────────────────────────────────────────────
    stepperSection: {
        background: '#FFFFFF',
        padding: 'clamp(36px, 4.5vw, 56px) 0',
    },
    stepperHeaderWrap: {
        maxWidth: '40ch',
    },
    stepsNavRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        marginTop: 44,
        padding: '0 clamp(0px, 2vw, 24px)',
        [MOBILE]: {
            gap: 4,
        },
    },
    stepNavBtn: {
        position: 'relative',
        display: 'grid',
        justifyItems: 'center',
        gap: 8,
        padding: '0 6px 14px',
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: '#1B2430',
        minWidth: 0,
        '&:focus-visible': {
            outline: '2px solid #ED6628',
            outlineOffset: 2,
        },
    },
    stepNavConnectingLine: {
        position: 'absolute',
        top: 26,
        left: '50%',
        width: '100%',
        height: 2,
        background: '#E1E4E9',
        zIndex: 0,
    },
    stepNavCircle: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 54,
        height: 54,
        borderRadius: '50%',
        fontSize: 17,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        zIndex: 1,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1), transform 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            transform: 'scale(1.06)',
        },
        [MOBILE]: {
            width: 44,
            height: 44,
            fontSize: 14,
        },
    },
    stepNavVerb: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    stepNavHeading: {
        fontSize: 14,
        lineHeight: 1.3,
        fontWeight: 700,
        textAlign: 'center',
        color: '#1B2430',
        [MOBILE]: {
            display: 'none',
        },
    },
    stepActiveBar: {
        position: 'absolute',
        left: '50%',
        bottom: 0,
        width: 36,
        height: 3,
        borderRadius: 2,
        transform: 'translateX(-50%)',
    },
    stepPanel: {
        marginTop: 28,
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        background: '#FFFFFF',
        boxShadow: '0 20px 50px rgba(23, 41, 57, 0.08)',
        padding: 'clamp(24px, 3vw, 40px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 40px)',
        alignItems: 'center',
    },
    stepVisualCol: {
        flex: '1 1 380px',
        minWidth: 0,
        margin: 0,
        display: 'grid',
        gap: 10,
    },
    stepVisualFrame: {
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid #E6E9EE',
        padding: '14px 14px 0',
    },
    stepTextCol: {
        flex: '1 1 400px',
        minWidth: 0,
        display: 'grid',
        gap: 18,
    },
    stepPill: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    stepTitle: {
        fontSize: 'clamp(24px, 2.6vw, 34px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    stepBodyText: {
        fontSize: 15.5,
        lineHeight: 1.65,
        margin: 0,
        color: '#5B6470',
        maxWidth: '90ch',
    },
    stepMetaGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 12,
    },
    stepMetaBox: {
        padding: '16px 18px',
        borderRadius: 14,
        background: '#FFF8F4',
        display: 'grid',
        gap: 6,
    },
    stepMetaLabel: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#A63C0C',
    },
    stepMetaValue: {
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
    },
    fwTiersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 10,
        [MOBILE]: {
            gridTemplateColumns: '1fr',
        },
    },
    fwTierCard: {
        padding: '14px 16px',
        borderRadius: 14,
        display: 'grid',
        gap: 6,
        alignContent: 'start',
    },
    stepControlsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 6,
    },
    stepNavBtnPrev: {
        height: 44,
        padding: '0 18px',
        borderRadius: 10,
        border: '1px solid #D5D9E0',
        background: '#FFFFFF',
        color: '#1B2430',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    stepNavBtnNext: {
        height: 44,
        padding: '0 18px',
        borderRadius: 10,
        border: 0,
        background: '#C45A2B',
        color: '#FFFFFF',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 200ms ease',
        '&:hover:not(:disabled)': {
            background: '#A63C0C',
        },
    },

    // ── SECTION 4: WHY CHOOSE IT ──────────────────────────────────────────────
    whySection: {
        background: '#F4F6F9',
        padding: 'clamp(36px, 4.5vw, 56px) 0',
    },
    whyGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
    },
    whyCard: {
        padding: 'clamp(22px, 2.6vw, 30px)',
        borderRadius: 20,
        display: 'grid',
        gap: 12,
        alignContent: 'start',
    },
    whyIconBox: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 46,
        height: 46,
        borderRadius: 12,
        background: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(23,41,57,0.06)',
    },
    whyCardTitle: {
        fontSize: 18,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 700,
        margin: '4px 0 0',
        color: '#1B2430',
    },
    whyCardBody: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#3F4752',
    },

    // ── SECTION 5: NEXT STEP / BOTTOM CTA ─────────────────────────────────────
    nextStepSection: {
        marginTop: 'clamp(24px, 3vw, 40px)',
        background: '#172939',
        color: '#FFFFFF',
    },
    nextStepContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    nextStepTextCol: {
        flex: '1 1 420px',
        minWidth: 0,
        display: 'grid',
        gap: 12,
    },
    nextStepTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '22ch',
        color: '#FFFFFF',
    },
    nextStepBody: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
        maxWidth: '60ch',
    },
    nextStepCtasRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    nextStepBtnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms ease',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
        },
    },
    nextStepBtnOutline: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: 'transparent',
        color: '#FFFFFF',
        border: '1px solid #5D7A96',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        transition: 'border-color 200ms ease',
        '&:hover': {
            borderColor: '#FFFFFF',
            color: '#FFFFFF',
        },
    },
}));

// Stage figure helper
const getStageFig = (learn) => {
    const names = ['Define', 'Measure', 'Prioritize', 'Build', 'Re-measure'];
    const on = learn ? [1, 1, 1, 1, 1] : [1, 1, 1, 0, 0];
    const stages = names.map((name, i) => {
        const active = !!on[i];
        const added = learn && i >= 3;
        return {
            n: String(i + 1),
            name,
            added,
            line: i < 4,
            lineColor: on[i] && on[i + 1] ? (learn && i >= 2 ? '#ED6628' : '#172939') : '#E6E9EE',
            bg: added ? '#ED6628' : active ? '#172939' : '#FFFFFF',
            fg: active ? '#FFFFFF' : '#B8C0CC',
            ring: added ? '#ED6628' : active ? '#172939' : '#E6E9EE',
            text: active ? '#1B2430' : '#B8C0CC',
        };
    });
    return {
        stages,
        figLabel: learn ? 'Measure + Learn covers all five stages: Define, Measure, Prioritize, Build and Re-measure' : 'Measure covers three stages: Define, Measure and Prioritize',
        figCaption: learn ? 'The full cycle: baseline, targeted upskilling, then measured improvement.' : 'Baseline and priorities; stages 4–5 are added with Measure + Learn.',
    };
};

// Data arrays
const CONFIGS = [
    {
        tag: 'Measure',
        h: 'Know where your workforce stands—and where to invest next.',
        intro: 'Measure creates a shared definition of AI-digital capacity across your organization, measures employees against the capabilities required for their roles and gives leaders an actionable readiness baseline.',
        best: 'Organizations that need a credible workforce-readiness baseline, role-level gap analysis and evidence for deciding where capability-building investment should focus.',
        cta: 'Configure Measure',
        featured: false,
        border: '#E6E9EE',
        bg: '#FFFFFF',
        tagBg: '#EEF3F8',
        tagColor: '#2A4057',
        ctaBg: '#FFFFFF',
        ctaColor: '#1B2430',
        ctaBorder: '#D5D9E0',
        ...getStageFig(false),
    },
    {
        tag: 'Measure + Learn',
        featured: true,
        h: 'Turn workforce gaps into targeted upskilling—and prove what improves.',
        intro: 'Measure + Learn includes everything in Measure, then adds LearnCreator to transform the gaps identified into role-specific, ready-to-deliver learning programs. Programs can be created from assessment results or from a strategic business priority set by your organization. Embedded measurement connects each program to the capabilities it is intended to improve.',
        best: 'Organizations that want an end-to-end system for measuring workforce readiness, building targeted upskilling and demonstrating improvement.',
        cta: 'Configure Measure + Learn',
        border: '#ED6628',
        bg: '#FFF8F4',
        tagBg: '#ED6628',
        tagColor: '#FFFFFF',
        ctaBg: '#ED6628',
        ctaColor: '#FFFFFF',
        ctaBorder: '#ED6628',
        ...getStageFig(true),
    },
];

const COMPARE_ROWS = [
    { f: 'Define role-based AI-digital capacity', a: 'Included', b: 'Included' },
    { f: 'Assess employees', a: 'Included', b: 'Included' },
    { f: 'Individual and executive dashboards', a: 'Included', b: 'Included' },
    { f: 'Identify workforce gaps and priorities', a: 'Included', b: 'Included' },
    { f: 'Create targeted upskilling programs', a: '—', b: 'Included' },
    { f: 'LearnCreator', a: '—', b: 'Included' },
    { f: 'Included creation capacity', a: '—', b: '1 unit per 10 employees' },
    { f: 'Measure program impact', a: '—', b: 'Included' },
    { f: 'Best suited to', a: 'Baseline and investment planning', b: 'Capability building and improvement' },
];

const ADDONS = [
    { h: 'DQ Stars', b: 'Independent validation of existing AI-literacy frameworks or programs' },
    { h: 'DQ Index Connect', b: 'Global benchmarking for executive and board-level reporting' },
    { h: 'Additional role-family mapping', b: 'Extend the framework across more specialized workforce groups' },
    { h: 'Localization', b: 'Adapt assessments, programs and interfaces for language and local context' },
    { h: 'Platform Mode', b: 'API, LMS, SSO and embedded deployment options' },
    { h: 'Custom reporting', b: 'Board, regulator, investor or program-specific reporting outputs' },
];

const STEPS_DATA = [
    {
        eyebrow: '1 · Define',
        h: 'FrameworkCreator',
        b: 'Establish one shared definition of AI-digital capacity across the organization and specify what each role family needs to know, apply and demonstrate. The framework can map existing organizational requirements or be customized to your strategy, roles and operating context.',
        inc: 'Measure and Measure + Learn',
        out: 'Organizational AI-digital capacity framework',
    },
    {
        eyebrow: '2 · Measure',
        h: 'AI-Digital Capacity Assessment',
        b: 'Assess every participating employee against the capabilities expected based on the defined framework. Individual scorecards help employees understand their strengths and development needs, while aggregated dashboards reveal gaps across roles, teams and the organization.',
        inc: 'Measure and Measure + Learn',
        out: 'Employee scorecards, organizational baseline and role-level gap analysis',
    },
    {
        eyebrow: '3 · Prioritize',
        h: 'Executive Dashboard',
        b: 'See where capability is strong, where gaps are concentrated and which roles or teams require the most immediate support. Leaders can use the evidence to prioritize investment, align workforce initiatives and establish measurable improvement targets.',
        inc: 'Measure and Measure + Learn',
        out: 'Leadership priorities and evidence-based capability-development plan',
    },
    {
        eyebrow: '4 · Build',
        h: 'LearnCreator',
        b: 'Turn assessed gaps—or a strategic business goal—into targeted, standards-aligned upskilling programs. Create learning for particular roles, teams or capability areas and adapt it to your industry, organizational context, language and preferred delivery format.',
        inc: 'Measure + Learn',
        out: 'Ready-to-deliver, role-specific workforce learning programs',
    },
    {
        eyebrow: '5 · Re-measure',
        h: 'Progress and Impact Dashboard',
        b: 'Measure employees again to determine what changed after learning and where further development is required. Track participation, program outcomes and capability improvement at employee, team, role and organizational levels.',
        inc: 'Measure + Learn',
        out: 'Evidence of improvement and the priorities for the next capability-building cycle',
    },
];

const FW_TIERS = [
    { h: 'Basic', b: 'The DQ framework, mapped to your roles.', n: '01', tint: '#E1F0F9', ink: '#1F5F86' },
    { h: 'Standard', b: 'Guided configuration of capabilities and role mappings.', n: '02', tint: '#EDF2D5', ink: '#4E6A12' },
    { h: 'Full', b: 'A bespoke, enterprise-wide framework.', n: '03', tint: '#FAEDDA', ink: '#8A5A10' },
];

const PROGRESS_ROWS = [
    { role: 'Data and analytics', before: '72%', after: '81%', delta: '+9' },
    { role: 'Finance', before: '61%', after: '70%', delta: '+9' },
    { role: 'Sales and marketing', before: '56%', after: '67%', delta: '+11' },
    { role: 'Operations', before: '48%', after: '58%', delta: '+10' },
    { role: 'Frontline supervisors', before: '39%', after: '52%', delta: '+13' },
];

const STEP_COLORS = ['#68C3EE', '#BCD253', '#EDB459', '#ED6628', '#B9316D'];
const STEP_TINTS = ['#E1F0F9', '#EDF2D5', '#FAEDDA', '#F8EAE4', '#F6E5F0'];
const STEP_INKS = ['#1F5F86', '#4E6A12', '#8A5A10', '#A63C0C', '#8E1F55'];

const WHY_LIST = [
    {
        h: 'Start with evidence—not assumptions',
        b: 'Measure workforce readiness before selecting or developing training. Investment is directed towards demonstrated needs rather than generic content.',
        tint: '#E1F0F9',
        line: '#C9E2F2',
        ink: '#1F5F86',
        icon: 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
    },
    {
        h: 'Define capability by role',
        b: 'An analyst, executive, salesperson and frontline supervisor do not require identical AI capabilities. The framework makes those differences explicit.',
        tint: '#EDF2D5',
        line: '#DCE6B8',
        ink: '#4E6A12',
        icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
    },
    {
        h: 'Connect measurement to action',
        b: 'Measure identifies where intervention is needed. Measure + Learn converts those findings into targeted learning and measures what improves.',
        tint: '#FAEDDA',
        line: '#F3DDB4',
        ink: '#8A5A10',
        icon: 'M5 12h14 M13 6l6 6-6 6',
    },
    {
        h: 'Give leaders one organizational view',
        b: 'Bring employee, team, role-family and organization-level results together in an executive dashboard designed to support decisions.',
        tint: '#F8EAE4',
        line: '#F0D1C3',
        ink: '#A63C0C',
        icon: 'M3 3v18h18 M7 14l4-4 4 4 5-6',
    },
    {
        h: 'Compare against a globally aligned foundation',
        b: 'The underlying capabilities are aligned with IEEE 3527.1™, enabling your internal framework and results to connect with a globally interoperable model of digital intelligence.',
        tint: '#F6E5F0',
        line: '#EBC9DB',
        ink: '#8E1F55',
        icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20 M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10',
    },
    {
        h: 'Build programs from your gaps',
        b: 'With Measure + Learn, upskilling is created from assessment evidence and business priorities—not selected blindly from an off-the-shelf catalog.',
        tint: '#E8EDF5',
        line: '#CFD9EA',
        ink: '#2A4057',
        icon: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20 M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z M9 7h7 M9 11h5',
    },
];

export default function WorkforceAiReadinessPage() {
    const classes = useStyles();
    const shared = useSharedStyles();
    const openAgentChat = useAgentChat();

    const [step, setStep] = useState(2);

    const cur = STEPS_DATA[step];
    const selColor = STEP_COLORS[step];
    const selTint = STEP_TINTS[step];
    const selInk = STEP_INKS[step];
    const selN = `0${step + 1}`;
    const selVerb = cur.eyebrow.split('· ')[1];

    const prevDisabled = step === 0;
    const nextDisabled = step === STEPS_DATA.length - 1;
    const nextLabel = step < STEPS_DATA.length - 1 ? `Next: ${STEPS_DATA[step + 1].eyebrow.split('· ')[1]} →` : 'Last step';

    return (
        <div className={shared.page} style={{ background: '#FFFFFF', color: '#1B2430' }}>
            <PbdqNav />

            {/* ── SECTION 0: HERO ────────────────────────────────────────── */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroContainer}>
                    <div className={classes.heroTextCol}>
                        <h1 className={classes.heroTitle}>
                            Turn AI investment into <span style={{ color: '#ED6628' }}>measurable workforce capability.</span>
                        </h1>
                        <p className={classes.heroLead}>
                            AI transformation is not only a technology project—it is a people-capability project. Workforce AI Readiness defines what AI-digital capacity each role requires, measures where employees currently stand and gives leaders a clear view of workforce strengths, gaps and priorities.
                        </p>
                        <button
                            type="button"
                            onClick={openAgentChat}
                            className={classes.heroBtn}
                        >
                            Build your solution
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <div className={classes.heroArtCol}>
                        <img
                            src={heroImg}
                            alt="Workforce AI Readiness solution and capability framework"
                            className={classes.heroImg}
                        />
                    </div>
                </div>
            </section>

            {/* ── SECTION 1: CHOOSE THE RIGHT CONFIGURATION ─────────────── */}
            <section id="configurations" aria-label="Choose the right configuration" className={classes.configSection}>
                <div className={classes.gutter}>
                    <span className={classes.sectionEyebrow}>Choose the right configuration</span>
                    <h2 className={classes.sectionTitle} style={{ maxWidth: '22ch' }}>
                        Start with the level of action your organization needs.
                    </h2>

                    <div className={classes.configGrid}>
                        {CONFIGS.map((c) => (
                            <div
                                key={c.tag}
                                className={classes.configCard}
                                style={{
                                    border: `1px solid ${c.border}`,
                                    background: c.bg,
                                }}
                            >
                                <span
                                    className={classes.configTag}
                                    style={{
                                        background: c.tagBg,
                                        color: c.tagColor,
                                    }}
                                >
                                    {c.tag}
                                </span>
                                <h3 className={classes.configCardTitle}>{c.h}</h3>
                                <p className={classes.configCardIntro}>{c.intro}</p>

                                <figure
                                    role="img"
                                    aria-label={c.figLabel}
                                    className={classes.configFigure}
                                >
                                    <div className={classes.configStagesRow}>
                                        {c.stages.map((s, idx) => (
                                            <div key={idx} className={classes.stageItem}>
                                                {s.line && (
                                                    <span
                                                        aria-hidden="true"
                                                        className={classes.stageLine}
                                                        style={{ background: s.lineColor }}
                                                    />
                                                )}
                                                <span
                                                    className={classes.stageCircle}
                                                    style={{
                                                        background: s.bg,
                                                        color: s.fg,
                                                        border: `2px solid ${s.ring}`,
                                                    }}
                                                >
                                                    {s.n}
                                                </span>
                                                <span
                                                    className={classes.stageName}
                                                    style={{ color: s.text }}
                                                >
                                                    {s.name}
                                                </span>
                                                {s.added && (
                                                    <span className={classes.stageAddedBadge}>
                                                        + Learn
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <figcaption className={classes.configFigCaption}>
                                        {c.figCaption}
                                    </figcaption>
                                </figure>

                                <div className={classes.configBestFor}>
                                    <p style={{ margin: 0 }}>
                                        <strong style={{ color: '#1B2430' }}>Best for:</strong> {c.best}
                                    </p>
                                </div>

                                <Link
                                    to={pbdqRoutes.pricing}
                                    className={classes.configCtaBtn}
                                    style={{
                                        background: c.ctaBg,
                                        color: c.ctaColor,
                                        borderColor: c.ctaBorder,
                                    }}
                                >
                                    {c.cta}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SECTION 2: MEASURE, OR MEASURE + LEARN? ────────────────── */}
            <section aria-label="Measure, or Measure + Learn?" className={classes.compareSection}>
                <div className={classes.gutter}>
                    <span className={classes.sectionEyebrow}>Measure, or Measure + Learn?</span>
                    <h2 className={classes.sectionTitle} style={{ margin: '0 0 32px', maxWidth: '22ch' }}>
                        One establishes the evidence. The other acts on it.
                    </h2>

                    <div className={classes.tableWrapper}>
                        <div className={classes.tableInner}>
                            <div className={classes.tableHeaderRow}>
                                <span>Feature</span>
                                <span>Measure</span>
                                <span style={{ color: '#ED6628' }}>Measure + Learn</span>
                            </div>
                            {COMPARE_ROWS.map((r, idx) => (
                                <div key={idx} className={classes.tableDataRow}>
                                    <span style={{ fontWeight: 600 }}>{r.f}</span>
                                    <span style={{ color: r.a === '—' ? '#B8C0CC' : '#1B2430' }}>{r.a}</span>
                                    <span style={{ color: r.b === '—' ? '#B8C0CC' : '#1B2430', fontWeight: 600 }}>{r.b}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className={classes.compareNoteBox}>
                        <strong style={{ color: '#1B2430' }}>Not sure which configuration you need?</strong> Start with Measure to establish the baseline. You can expand into Measure + Learn when you are ready to build targeted programs from the gaps found.
                    </p>

                    <div className={classes.addonsCard}>
                        <span className={classes.sectionEyebrow} style={{ marginBottom: 20 }}>Available add-ons</span>
                        <div className={classes.addonsGrid}>
                            {ADDONS.map((a, idx) => (
                                <p key={idx} className={classes.addonItem}>
                                    <strong style={{ color: '#1B2430' }}>{a.h}</strong> — {a.b}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SECTION 3: HOW IT WORKS (STEPPER) ─────────────────────── */}
            <section aria-label="How it works" className={classes.stepperSection}>
                <div className={classes.gutter}>
                    <span className={classes.sectionEyebrow}>How it works</span>
                    <h2 className={classes.sectionTitle} style={{ maxWidth: '40ch' }}>
                        <span style={{ display: 'block' }}>Move from AI ambition to a measurable</span>
                        <span style={{ display: 'block' }}>workforce-capability system.</span>
                    </h2>

                    {/* Step Tabs */}
                    <div role="tablist" aria-label="Steps" className={classes.stepsNavRow}>
                        {STEPS_DATA.map((s, idx) => {
                            const isSelected = idx === step;
                            const color = STEP_COLORS[idx];
                            const tint = STEP_TINTS[idx];
                            const ink = STEP_INKS[idx];
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    role="tab"
                                    aria-selected={isSelected}
                                    onClick={() => setStep(idx)}
                                    className={classes.stepNavBtn}
                                >
                                    {idx < 4 && <span aria-hidden="true" className={classes.stepNavConnectingLine} />}
                                    <span
                                        className={classes.stepNavCircle}
                                        style={{
                                            background: isSelected ? color : tint,
                                            color: isSelected ? '#FFFFFF' : '#1B2430',
                                            border: `2px solid ${color}`,
                                        }}
                                    >
                                        {`0${idx + 1}`}
                                    </span>
                                    <span
                                        className={classes.stepNavVerb}
                                        style={{ color: ink }}
                                    >
                                        {s.eyebrow.split('· ')[1]}
                                    </span>
                                    <span className={classes.stepNavHeading}>
                                        {s.h}
                                    </span>
                                    {isSelected && (
                                        <span
                                            aria-hidden="true"
                                            className={classes.stepActiveBar}
                                            style={{ background: color }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Step Content Panel */}
                    <div
                        role="tabpanel"
                        className={classes.stepPanel}
                        style={{ borderTop: `4px solid ${selColor}` }}
                    >
                        <figure className={classes.stepVisualCol}>
                            <div
                                className={classes.stepVisualFrame}
                                style={{ background: selTint }}
                            >
                                {step === 0 && (
                                    <video
                                        src={demoVideo}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        controls
                                        aria-label="FrameworkCreator: mapping a framework to the DQ standard"
                                        style={{
                                            width: '100%',
                                            aspectRatio: '16/10',
                                            objectFit: 'cover',
                                            objectPosition: 'top left',
                                            display: 'block',
                                            borderRadius: '10px 10px 0 0',
                                            border: '1px solid #E6E9EE',
                                            borderBottom: 0,
                                            background: '#FFFFFF',
                                            boxShadow: '0 12px 30px rgba(23,41,57,0.12)',
                                        }}
                                    />
                                )}

                                {step === 1 && (
                                    <div style={{ position: 'relative', aspectRatio: '16/10' }}>
                                        {/* Mobile phone mock */}
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: 'absolute',
                                                right: '6%',
                                                bottom: 0,
                                                width: '27%',
                                                minWidth: 96,
                                                aspectRatio: '9/18.5',
                                                borderRadius: 22,
                                                background: '#1B2430',
                                                padding: 6,
                                                boxShadow: '0 18px 40px rgba(23,41,57,0.28)',
                                                zIndex: 2,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: 17,
                                                    background: '#FFFFFF',
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    fontSize: 7,
                                                    color: '#1B2430',
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '5px 0 0' }}>
                                                    <span style={{ width: '34%', height: 5, borderRadius: 999, background: '#1B2430' }} />
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 9px 6px', borderBottom: '1px solid #EEF1F4' }}>
                                                    <img src={HomeHeroDqLogo} alt="" style={{ height: 9, width: 'auto' }} />
                                                    <span style={{ color: '#6B7280', fontSize: 6.5 }}>14 / 40</span>
                                                </div>
                                                <div style={{ height: 3, background: '#EEF1F4' }}>
                                                    <div style={{ width: '35%', height: '100%', background: '#ED6628' }} />
                                                </div>
                                                <div style={{ flex: 1, padding: 9, display: 'grid', gap: 6, alignContent: 'start' }}>
                                                    <span style={{ justifySelf: 'start', padding: '2px 6px', borderRadius: 999, background: '#E1F0F9', color: '#1F5F86', fontSize: 5.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                                        Data &amp; AI judgement
                                                    </span>
                                                    <span style={{ display: 'block', height: 6, width: '94%', borderRadius: 999, background: '#D9DEE5' }} />
                                                    <span style={{ display: 'block', height: 6, width: '70%', borderRadius: 999, background: '#D9DEE5', marginTop: -2 }} />
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: 6, border: '1px solid #E6E9EE', borderRadius: 6, marginTop: 2 }}>
                                                        <span style={{ width: 7, height: 7, borderRadius: '50%', border: '1px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 5, width: '60%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: 6, border: '1px solid #172939', borderRadius: 6, background: '#EEF3F8' }}>
                                                        <span style={{ width: 7, height: 7, borderRadius: '50%', border: '2.5px solid #172939', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 5, width: '72%', borderRadius: 999, background: '#B7C8DA' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: 6, border: '1px solid #E6E9EE', borderRadius: 6 }}>
                                                        <span style={{ width: 7, height: 7, borderRadius: '50%', border: '1px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 5, width: '48%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: 6, border: '1px solid #E6E9EE', borderRadius: 6 }}>
                                                        <span style={{ width: 7, height: 7, borderRadius: '50%', border: '1px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 5, width: '54%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                </div>
                                                <div style={{ padding: '8px 9px 10px' }}>
                                                    <span style={{ display: 'block', padding: '6px 0', borderRadius: 999, background: '#ED6628', color: '#FFFFFF', fontWeight: 700, textAlign: 'center', fontSize: 6.5 }}>
                                                        Next →
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Desktop screen mock */}
                                        <div
                                            role="img"
                                            aria-label="AI-Digital Capacity Assessment on desktop and mobile: a scenario item with answer options and progress"
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: '10px 10px 0 0',
                                                border: '1px solid #E6E9EE',
                                                borderBottom: 0,
                                                background: '#FFFFFF',
                                                boxShadow: '0 12px 30px rgba(23,41,57,0.12)',
                                                overflow: 'hidden',
                                                fontSize: 11,
                                                color: '#1B2430',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderBottom: '1px solid #EEF1F4' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 11.5 }}>
                                                    <img src={HomeHeroDqLogo} alt="" style={{ height: 14, width: 'auto' }} />
                                                    AI-Digital Capacity Assessment
                                                </span>
                                                <span style={{ color: '#6B7280' }}>Item 14 of 40 · ~12 min left</span>
                                            </div>
                                            <div style={{ height: 4, background: '#EEF1F4' }}>
                                                <div style={{ width: '35%', height: '100%', background: '#ED6628' }} />
                                            </div>
                                            <div style={{ flex: 1, padding: '16px 18px', display: 'grid', gap: 12, alignContent: 'start' }}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                    <span style={{ padding: '3px 8px', borderRadius: 999, background: '#E1F0F9', color: '#1F5F86', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                                        Data &amp; AI judgement
                                                    </span>
                                                    <span style={{ padding: '3px 8px', borderRadius: 999, background: '#F4F6F9', color: '#6B7280', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                                        Scenario
                                                    </span>
                                                </div>
                                                <div aria-hidden="true" style={{ display: 'grid', gap: 7, maxWidth: '46ch' }}>
                                                    <span style={{ display: 'block', height: 11, width: '92%', borderRadius: 999, background: '#D9DEE5' }} />
                                                    <span style={{ display: 'block', height: 11, width: '64%', borderRadius: 999, background: '#D9DEE5' }} />
                                                </div>
                                                <div aria-hidden="true" style={{ display: 'grid', gap: 6 }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid #E6E9EE', borderRadius: 8 }}>
                                                        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 8, width: '58%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1.5px solid #172939', borderRadius: 8, background: '#EEF3F8' }}>
                                                        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '4px solid #172939', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 8, width: '70%', borderRadius: 999, background: '#B7C8DA' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid #E6E9EE', borderRadius: 8 }}>
                                                        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 8, width: '46%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: '1px solid #E6E9EE', borderRadius: 8 }}>
                                                        <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px solid #B8C0CC', flex: 'none' }} />
                                                        <span style={{ display: 'block', height: 8, width: '52%', borderRadius: 999, background: '#E6E9EE' }} />
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 18px', borderTop: '1px solid #EEF1F4', color: '#6B7280' }}>
                                                <span>← Previous</span>
                                                <span style={{ padding: '7px 14px', borderRadius: 999, background: '#ED6628', color: '#FFFFFF', fontWeight: 700 }}>Next →</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {step === 2 && (
                                    <img
                                        loading="lazy"
                                        src={workforceExecutiveDashImg}
                                        alt="Executive dashboard: AI-digital readiness by role with baseline, priority roles and change since baseline"
                                        style={{
                                            width: '100%',
                                            aspectRatio: '16/10',
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            display: 'block',
                                            borderRadius: '10px 10px 0 0',
                                            border: '1px solid #E6E9EE',
                                            borderBottom: 0,
                                            boxShadow: '0 12px 30px rgba(23,41,57,0.12)',
                                        }}
                                    />
                                )}

                                {step === 3 && (
                                    <img
                                        loading="lazy"
                                        src={step4Img}
                                        alt="LearnCreator: create and assess AI-digital literacy programs, instantly"
                                        style={{
                                            width: '100%',
                                            aspectRatio: '16/10',
                                            objectFit: 'cover',
                                            objectPosition: 'center',
                                            display: 'block',
                                            borderRadius: '10px 10px 0 0',
                                            border: '1px solid #E6E9EE',
                                            borderBottom: 0,
                                            boxShadow: '0 12px 30px rgba(23,41,57,0.12)',
                                        }}
                                    />
                                )}

                                {step === 4 && (
                                    <div
                                        role="img"
                                        aria-label="Progress and impact dashboard: readiness before and after the program by role, overall gain and participation"
                                        style={{
                                            aspectRatio: '16/10',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: '10px 10px 0 0',
                                            border: '1px solid #E6E9EE',
                                            borderBottom: 0,
                                            background: '#FFFFFF',
                                            boxShadow: '0 12px 30px rgba(23,41,57,0.12)',
                                            overflow: 'hidden',
                                            color: '#1B2430',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #EEF1F4' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700 }}>
                                                <img src={HomeHeroDqLogo} alt="" style={{ height: 14, width: 'auto' }} />
                                                Executive dashboard · Progress and impact
                                            </span>
                                            <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 999, background: '#F4F6F9', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                                Sample data
                                            </span>
                                        </div>
                                        <div style={{ flex: 1, padding: '14px 16px', display: 'grid', gap: 9, alignContent: 'start' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                                                <div style={{ padding: '9px 11px', borderRadius: 10, background: '#F8F9FB' }}>
                                                    <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>Baseline → now</span>
                                                    <span style={{ display: 'block', fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>55 → 64</span>
                                                </div>
                                                <div style={{ padding: '9px 11px', borderRadius: 10, background: '#FDECE3' }}>
                                                    <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#A63C0C' }}>Capability gain</span>
                                                    <span style={{ display: 'block', fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 3, color: '#A63C0C', fontVariantNumeric: 'tabular-nums' }}>+9 pts</span>
                                                </div>
                                                <div style={{ padding: '9px 11px', borderRadius: 10, background: '#F8F9FB' }}>
                                                    <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>Completed program</span>
                                                    <span style={{ display: 'block', fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>82%</span>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', marginTop: 2 }}>
                                                <p style={{ fontSize: 11.5, fontWeight: 700, margin: 0 }}>Readiness by role · before and after</p>
                                                <span style={{ display: 'inline-flex', gap: 10, fontSize: 9.5, color: '#6B7280' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: '#B7C8DA' }} />
                                                        Before
                                                    </span>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: '#ED6628' }} />
                                                        After
                                                    </span>
                                                </span>
                                            </div>
                                            {PROGRESS_ROWS.map((r, idx) => (
                                                <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 36%) 1fr 34px', gap: 10, alignItems: 'center', fontSize: 10.5 }}>
                                                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.role}</span>
                                                    <span style={{ display: 'grid', gap: 2 }}>
                                                        <span style={{ height: 5, width: r.before, borderRadius: 999, background: '#B7C8DA' }} />
                                                        <span style={{ height: 5, width: r.after, borderRadius: 999, background: '#ED6628' }} />
                                                    </span>
                                                    <span style={{ fontWeight: 800, textAlign: 'right', color: '#A63C0C', fontVariantNumeric: 'tabular-nums' }}>{r.delta}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </figure>

                        <div className={classes.stepTextCol}>
                            <span
                                className={classes.stepPill}
                                style={{ background: selTint, color: selInk }}
                            >
                                Step {selN} · {selVerb}
                            </span>
                            <h3 className={classes.stepTitle}>{cur.h}</h3>
                            <p className={classes.stepBodyText}>{cur.b}</p>

                            <div className={classes.stepMetaGrid}>
                                <div className={classes.stepMetaBox}>
                                    <span className={classes.stepMetaLabel}>Included in</span>
                                    <span className={classes.stepMetaValue}>{cur.inc}</span>
                                </div>
                                <div className={classes.stepMetaBox}>
                                    <span className={classes.stepMetaLabel}>Output</span>
                                    <span className={classes.stepMetaValue}>{cur.out}</span>
                                </div>
                            </div>

                            {step === 0 && (
                                <div style={{ display: 'grid', gap: 12, paddingTop: 6 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2A4057' }}>
                                        FrameworkCreator options
                                    </span>
                                    <div className={classes.fwTiersGrid}>
                                        {FW_TIERS.map((f, idx) => (
                                            <div
                                                key={idx}
                                                className={classes.fwTierCard}
                                                style={{ background: f.tint }}
                                            >
                                                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: f.ink }}>
                                                    Tier {f.n}
                                                </span>
                                                <h4 style={{ fontSize: 16, lineHeight: 1.2, letterSpacing: '-0.015em', fontWeight: 800, margin: 0 }}>
                                                    {f.h}
                                                </h4>
                                                <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, color: '#1B2430' }}>
                                                    {f.b}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className={classes.stepControlsRow}>
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    disabled={prevDisabled}
                                    className={classes.stepNavBtnPrev}
                                    style={{ opacity: prevDisabled ? 0.45 : 1 }}
                                >
                                    ← Previous
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setStep((s) => Math.min(STEPS_DATA.length - 1, s + 1))}
                                    disabled={nextDisabled}
                                    className={classes.stepNavBtnNext}
                                    style={{ opacity: nextDisabled ? 0.45 : 1 }}
                                >
                                    {nextLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SECTION 4: WHY ORGANIZATIONS CHOOSE IT ────────────────── */}
            <section aria-label="Why organizations choose it" className={classes.whySection}>
                <div className={classes.gutter}>
                    <span className={classes.sectionEyebrow}>Why organizations choose it</span>
                    <h2 className={classes.sectionTitle} style={{ maxWidth: '22ch' }}>
                        Why this instead of an AI training catalog?
                    </h2>

                    <div className={classes.whyGrid}>
                        {WHY_LIST.map((w, idx) => (
                            <div
                                key={idx}
                                className={classes.whyCard}
                                style={{
                                    background: w.tint,
                                    border: `1px solid ${w.line}`,
                                }}
                            >
                                <span
                                    className={classes.whyIconBox}
                                    style={{ color: w.ink }}
                                >
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d={w.icon} />
                                    </svg>
                                </span>
                                <h3 className={classes.whyCardTitle}>{w.h}</h3>
                                <p className={classes.whyCardBody}>{w.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SECTION 5: NEXT STEP ───────────────────────────────────── */}
            <section aria-label="Next step" className={classes.nextStepSection}>
                <div className={classes.nextStepContainer}>
                    <div className={classes.nextStepTextCol}>
                        <h2 className={classes.nextStepTitle}>
                            Start by measuring readiness. Continue by building capability.
                        </h2>
                        <p className={classes.nextStepBody}>
                            Choose Measure to establish your workforce baseline and investment priorities. Choose Measure + Learn to add targeted program creation, delivery and measurable improvement.
                        </p>
                    </div>

                    <div className={classes.nextStepCtasRow}>
                        <button
                            type="button"
                            onClick={openAgentChat}
                            className={classes.nextStepBtnPrimary}
                        >
                            Build your solution
                        </button>
                        <Link
                            to={pbdqRoutes.getStarted}
                            className={classes.nextStepBtnOutline}
                        >
                            Get an indicative price
                        </Link>
                    </div>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
}
